import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  applyFieldEnhancement,
  translateEnhancedOption
} from '../../constants/listingFieldEnhancements';

const isPlainObject = (value) => Object.prototype.toString.call(value) === '[object Object]';

const areArraysEqual = (left, right) => {
  if (left === right) return true;
  if (left.length !== right.length) return false;

  return left.every((item, index) => item === right[index]);
};

const areDetailsEqual = (left, right) => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  if (leftKeys.length !== rightKeys.length) return false;

  return leftKeys.every((key) => left[key] === right[key]);
};

export const normalizeListingDetailsValue = (rawValue) => {
  if (!rawValue) return {};

  if (typeof rawValue === 'string') {
    try {
      const parsedValue = JSON.parse(rawValue);
      return isPlainObject(parsedValue) ? parsedValue : {};
    } catch {
      return {};
    }
  }

  return isPlainObject(rawValue) ? rawValue : {};
};

const parseFieldOptions = (rawOptions) => {
  if (Array.isArray(rawOptions)) {
    return rawOptions.filter((option) => option !== null && option !== undefined).map(String);
  }

  if (typeof rawOptions === 'string') {
    try {
      const parsedOptions = JSON.parse(rawOptions);
      return Array.isArray(parsedOptions)
        ? parsedOptions.filter((option) => option !== null && option !== undefined).map(String)
        : [];
    } catch {
      return [];
    }
  }

  return [];
};

const formatFieldLabel = (definition, translatedLabel) => {
  if (!definition?.icon) {
    return translatedLabel;
  }

  return `${definition.icon} ${translatedLabel}`;
};

const sanitizeListingDetailDefinitions = (definitions) => {
  if (!Array.isArray(definitions)) return [];

  return definitions
    .filter((definition) => isPlainObject(definition) && typeof definition.key === 'string' && definition.key.trim())
    .map((definition) => {
      const base = {
        ...definition,
        key: definition.key.trim(),
        field_type: definition.field_type || 'text',
        options: parseFieldOptions(definition.options)
      };
      // Appliquer les enrichissements frontend (select forcé, min/max…)
      return applyFieldEnhancement(base);
    });
};

const getDefaultFieldValue = (definition) => {
  const defaultValue = definition?.default_value;

  if (definition?.field_type === 'boolean') {
    return defaultValue === true || defaultValue === 'true';
  }

  if (definition?.field_type === 'number') {
    if (defaultValue === '' || defaultValue === null || defaultValue === undefined) {
      return '';
    }

    const parsedNumber = Number(defaultValue);
    return Number.isNaN(parsedNumber) ? '' : parsedNumber;
  }

  return defaultValue ?? '';
};

const coerceFieldValue = (definition, rawValue) => {
  if (definition?.field_type === 'boolean') {
    return rawValue === true || rawValue === 'true';
  }

  if (definition?.field_type === 'number') {
    if (rawValue === '' || rawValue === null || rawValue === undefined) {
      return '';
    }

    const parsedNumber = Number(rawValue);
    return Number.isNaN(parsedNumber) ? rawValue : parsedNumber;
  }

  return rawValue ?? '';
};

const validateListingDetailField = (definition, fieldValue, t) => {
  if (!definition) return null;

  if (definition.field_type === 'boolean') {
    return null;
  }

  if (definition.field_type === 'select') {
    if (fieldValue === '' || fieldValue === null || fieldValue === undefined) {
      return t('listing:details.errors.selectRequired', 'Ce champ est obligatoire.');
    }

    if (definition.options.length > 0 && !definition.options.includes(String(fieldValue))) {
      return t('listing:details.errors.selectRequired', 'Ce champ est obligatoire.');
    }

    return null;
  }

  if (definition.field_type === 'number') {
    if (fieldValue === '' || fieldValue === null || fieldValue === undefined) {
      return t('listing:details.errors.numberRequired', 'Veuillez entrer une valeur numérique.');
    }

    const parsedNumber = Number(fieldValue);
    if (Number.isNaN(parsedNumber)) {
      return t('listing:details.errors.numberInvalid', 'Ceci doit être un nombre valide.');
    }

    if (definition.min !== undefined && parsedNumber < definition.min) {
      return t('listing:details.errors.numberMin', { min: definition.min, unit: definition.unit ? ` ${definition.unit}` : '' });
    }

    if (definition.max !== undefined && parsedNumber > definition.max) {
      return t('listing:details.errors.numberMax', { max: definition.max, unit: definition.unit ? ` ${definition.unit}` : '' });
    }

    return null;
  }

  if (typeof fieldValue !== 'string' || fieldValue.trim() === '') {
    return t('listing:details.errors.textRequired', 'Ce champ ne peut pas être vide.');
  }

  return null;
};

export const validateListingDetailsValues = ({
  fieldDefs,
  details,
  t,
  loading = false,
  fetchError = null
}) => {
  if (loading) {
    return {
      isValid: false,
      reason: 'loading',
      errors: {},
      firstErrorKey: null,
      message: t('listing:details.loading', 'Chargement des détails spécifiques...')
    };
  }

  if (fetchError) {
    return {
      isValid: false,
      reason: 'fetch',
      errors: {},
      firstErrorKey: null,
      message: t(
        'listing:details.fetchBlocked',
        'Impossible de valider les détails tant que le chargement des champs a échoué.'
      )
    };
  }

  const normalizedDefinitions = sanitizeListingDetailDefinitions(fieldDefs);
  const normalizedDetails = normalizeListingDetailsValue(details);
  const errors = {};

  normalizedDefinitions.forEach((definition) => {
    if (!Object.prototype.hasOwnProperty.call(normalizedDetails, definition.key)) {
      return;
    }

    const fieldError = validateListingDetailField(definition, normalizedDetails[definition.key], t);
    if (fieldError) {
      errors[definition.key] = fieldError;
    }
  });

  const firstErrorKey = Object.keys(errors)[0] || null;

  return {
    isValid: firstErrorKey === null,
    reason: firstErrorKey ? 'fields' : null,
    errors,
    firstErrorKey,
    message: firstErrorKey
      ? t('listing:details.errors.formInvalid', 'Veuillez corriger les erreurs ci-dessous.')
      : null
  };
};

function ListingDetailsEditorInner(
  {
    offerType,
    value = {},
    onChange,
    onDefinitionsChange,
    onStatusChange
  },
  ref
) {
  const { t, i18n } = useTranslation(['listing', 'common', 'listingFields']);
  const normalizedValue = useMemo(() => normalizeListingDetailsValue(value), [value]);

  const [fieldDefs, setFieldDefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [activeKeys, setActiveKeys] = useState([]);
  const [reloadToken, setReloadToken] = useState(0);
  const [hasLoadedDefinitions, setHasLoadedDefinitions] = useState(false);

  const containerRef = useRef(null);
  const fieldRefs = useRef({});

  const scrollToElement = useCallback((element) => {
    if (!element) return;

    const targetTop = element.getBoundingClientRect().top + window.scrollY - 120;
    window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
  }, []);

  const scrollToContainer = useCallback(() => {
    if (containerRef.current) {
      scrollToElement(containerRef.current);
    }
  }, [scrollToElement]);

  const scrollToField = useCallback((fieldKey) => {
    const fieldElement = fieldRefs.current[fieldKey];

    if (fieldElement) {
      scrollToElement(fieldElement);
      return;
    }

    scrollToContainer();
  }, [scrollToContainer, scrollToElement]);

  useEffect(() => {
    if (typeof onDefinitionsChange === 'function') {
      onDefinitionsChange(fieldDefs);
    }
  }, [fieldDefs, onDefinitionsChange]);

  useEffect(() => {
    if (typeof onStatusChange === 'function') {
      onStatusChange({ loading, fetchError });
    }
  }, [loading, fetchError, onStatusChange]);

  useEffect(() => {
    let isCurrent = true;

    const loadDefinitions = async () => {
      if (!offerType) {
        if (!isCurrent) return;

        setFieldDefs([]);
        setFetchError(null);
        setFieldErrors({});
        setActiveKeys([]);
        setLoading(false);
        setHasLoadedDefinitions(false);
        return;
      }

      setLoading(true);
      setFetchError(null);
      setHasLoadedDefinitions(false);

      try {
        const response = await api.get('/listing-field-definitions', {
          params: { offer_type: offerType }
        });

        if (response.data?.success === false) {
          throw new Error('Failed to load listing detail definitions');
        }

        const nextDefinitions = sanitizeListingDetailDefinitions(response.data?.data || []);
        if (!isCurrent) return;

        setFieldDefs(nextDefinitions);
      } catch {
        if (!isCurrent) return;

        setFieldDefs([]);
        setFetchError(t('listing:details.fetchError', 'Impossible de charger les champs'));
      } finally {
        if (!isCurrent) return;

        setLoading(false);
        setHasLoadedDefinitions(true);
      }
    };

    loadDefinitions();

    return () => {
      isCurrent = false;
    };
  }, [offerType, reloadToken, t]);

  useEffect(() => {
    if (!offerType || !hasLoadedDefinitions) {
      return;
    }

    const allowedKeys = new Set(fieldDefs.map((definition) => definition.key));
    const sanitizedDetails = {};

    Object.entries(normalizedValue).forEach(([key, fieldValue]) => {
      if (allowedKeys.has(key)) {
        sanitizedDetails[key] = fieldValue;
      }
    });

    const nextActiveKeys = fieldDefs
      .filter((definition) => Object.prototype.hasOwnProperty.call(sanitizedDetails, definition.key))
      .map((definition) => definition.key);

    setActiveKeys((previousKeys) => (areArraysEqual(previousKeys, nextActiveKeys) ? previousKeys : nextActiveKeys));

    setFieldErrors((previousErrors) => {
      const nextErrors = {};
      Object.entries(previousErrors).forEach(([key, message]) => {
        if (nextActiveKeys.includes(key)) {
          nextErrors[key] = message;
        }
      });

      return areDetailsEqual(previousErrors, nextErrors) ? previousErrors : nextErrors;
    });

    if (!loading && !fetchError && !areDetailsEqual(sanitizedDetails, normalizedValue)) {
      onChange(sanitizedDetails);
    }
  }, [offerType, hasLoadedDefinitions, fieldDefs, normalizedValue, loading, fetchError, onChange]);

  const validateAllFields = useCallback((options = {}) => {
    const validationResult = validateListingDetailsValues({
      fieldDefs,
      details: normalizedValue,
      t,
      loading,
      fetchError
    });

    setFieldErrors(validationResult.errors);

    if (options.shouldScroll !== false && !validationResult.isValid) {
      window.setTimeout(() => {
        if (validationResult.firstErrorKey) {
          scrollToField(validationResult.firstErrorKey);
          return;
        }

        scrollToContainer();
      }, 80);
    }

    return validationResult;
  }, [fieldDefs, normalizedValue, t, loading, fetchError, scrollToContainer, scrollToField]);

  useImperativeHandle(ref, () => ({
    validateAllFields,
    scrollToFirstError: (fieldKey) => {
      const fallbackFieldKey = fieldKey || Object.keys(fieldErrors)[0];

      if (fallbackFieldKey) {
        scrollToField(fallbackFieldKey);
        return;
      }

      scrollToContainer();
    },
    focusSection: scrollToContainer,
    clearValidationErrors: () => setFieldErrors({}),
    hasValidationErrors: () => Object.keys(fieldErrors).length > 0 || Boolean(fetchError),
    getDefinitions: () => fieldDefs
  }), [validateAllFields, fieldErrors, fetchError, scrollToContainer, scrollToField, fieldDefs]);

  const handleRetry = useCallback(() => {
    setReloadToken((previousToken) => previousToken + 1);
  }, []);

  const handleChipClick = useCallback((fieldKey) => {
    const definition = fieldDefs.find((fieldDefinition) => fieldDefinition.key === fieldKey);
    if (!definition) return;

    setActiveKeys((previousKeys) => (previousKeys.includes(fieldKey) ? previousKeys : [...previousKeys, fieldKey]));
    onChange({
      ...normalizedValue,
      [fieldKey]: getDefaultFieldValue(definition)
    });

    setFieldErrors((previousErrors) => {
      if (!previousErrors[fieldKey]) {
        return previousErrors;
      }

      const nextErrors = { ...previousErrors };
      delete nextErrors[fieldKey];
      return nextErrors;
    });
  }, [fieldDefs, normalizedValue, onChange]);

  const handleRemoveField = useCallback((fieldKey) => {
    const nextDetails = { ...normalizedValue };
    delete nextDetails[fieldKey];

    setActiveKeys((previousKeys) => previousKeys.filter((key) => key !== fieldKey));
    onChange(nextDetails);

    setFieldErrors((previousErrors) => {
      if (!previousErrors[fieldKey]) {
        return previousErrors;
      }

      const nextErrors = { ...previousErrors };
      delete nextErrors[fieldKey];
      return nextErrors;
    });
  }, [normalizedValue, onChange]);

  const handleFieldChange = useCallback((fieldKey, rawValue) => {
    const definition = fieldDefs.find((fieldDefinition) => fieldDefinition.key === fieldKey);
    if (!definition) return;

    const nextValue = coerceFieldValue(definition, rawValue);
    onChange({
      ...normalizedValue,
      [fieldKey]: nextValue
    });

    if (fieldErrors[fieldKey]) {
      const fieldError = validateListingDetailField(definition, nextValue, t);

      setFieldErrors((previousErrors) => {
        const nextErrors = { ...previousErrors };

        if (fieldError) {
          nextErrors[fieldKey] = fieldError;
        } else {
          delete nextErrors[fieldKey];
        }

        return nextErrors;
      });
    }
  }, [fieldDefs, normalizedValue, onChange, fieldErrors, t]);

  const activeFieldDefs = useMemo(
    () => fieldDefs.filter((definition) => activeKeys.includes(definition.key)),
    [fieldDefs, activeKeys]
  );

  const availableFieldDefs = useMemo(
    () => fieldDefs.filter((definition) => !activeKeys.includes(definition.key)),
    [fieldDefs, activeKeys]
  );

  const translateOption = useCallback(
    (option, definition) => {
      // 1. Vérifier d'abord les traductions locales de l'enhancement
      if (definition?._optionI18n) {
        const local = translateEnhancedOption(option, definition, i18n.language);
        if (local !== null) return local;
      }
      // 2. Fallback sur le namespace listingFields chargé depuis l'API
      return t(`listingFields:opt_${option}`, option);
    },
    [t, i18n.language]
  );

  if (!offerType) {
    return null;
  }

  return (
    <Paper
      ref={containerRef}
      variant="outlined"
      sx={{
        mt: 3,
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid rgba(98, 0, 238, 0.16)',
        background: 'linear-gradient(180deg, rgba(250, 247, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%)',
        boxShadow: '0 18px 40px rgba(98, 0, 238, 0.08)'
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2.25,
          background: 'linear-gradient(135deg, rgba(98, 0, 238, 0.95) 0%, rgba(3, 218, 196, 0.92) 100%)',
          color: '#ffffff'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1.5,
            flexDirection: { xs: 'column', sm: 'row' }
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {t('listing:details.title', 'Détails spécifiques')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.6, opacity: 0.9, maxWidth: 760 }}>
              {t(
                'listing:details.helper',
                'Ajoutez uniquement les caractéristiques utiles à la compréhension de l’annonce, puis complétez-les avant de continuer.'
              )}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={t('listing:details.selectedCount', {
                count: activeFieldDefs.length,
                defaultValue: 'Sélectionnés: {{count}}'
              })}
              size="small"
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.35)',
                backgroundColor: 'rgba(255,255,255,0.14)'
              }}
              variant="outlined"
            />
            <Chip
              label={t('listing:details.availableCount', {
                count: availableFieldDefs.length,
                defaultValue: 'Disponibles: {{count}}'
              })}
              size="small"
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.35)',
                backgroundColor: 'rgba(255,255,255,0.14)'
              }}
              variant="outlined"
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {fetchError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={handleRetry}
              >
                {t('listing:details.retry', 'Réessayer')}
              </Button>
            }
          >
            {fetchError}
          </Alert>
        )}

        {Object.keys(fieldErrors).length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('listing:details.errors.formInvalid', 'Veuillez corriger les erreurs ci-dessous.')}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ py: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              {t('listing:details.loading', 'Chargement des détails spécifiques...')}
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                p: 2,
                borderRadius: 2.5,
                border: '1px solid rgba(98, 0, 238, 0.12)',
                background: 'linear-gradient(135deg, rgba(98, 0, 238, 0.05) 0%, rgba(3, 218, 196, 0.06) 100%)'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {t('listing:details.availableTitle', 'Champs disponibles')}
              </Typography>

              {availableFieldDefs.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {availableFieldDefs.map((definition) => (
                    <Chip
                      key={definition.key}
                      label={formatFieldLabel(definition, t(`listingFields:${definition.key}`, definition.label))}
                      onClick={() => handleChipClick(definition.key)}
                      clickable
                      color="primary"
                      variant="outlined"
                      sx={{
                        borderRadius: 999,
                        backgroundColor: '#ffffff',
                        '&:hover': {
                          backgroundColor: 'rgba(98, 0, 238, 0.08)'
                        }
                      }}
                    />
                  ))}
                </Box>
              ) : fieldDefs.length > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('listing:details.allSelected', 'Tous les détails disponibles sont déjà ajoutés.')}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('listing:details.noDefinitions', 'Aucun détail spécifique n’est disponible pour cette catégorie.')}
                </Typography>
              )}
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                {t('listing:details.selectedTitle', 'Détails sélectionnés')}
              </Typography>

              {activeFieldDefs.length === 0 ? (
                <Box
                  sx={{
                    px: 2.25,
                    py: 3,
                    borderRadius: 2.5,
                    border: '1px dashed rgba(98, 0, 238, 0.24)',
                    background: 'rgba(98, 0, 238, 0.02)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {t('listing:details.emptyTitle', 'Aucun détail actif')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    {t(
                      'listing:details.emptyDescription',
                      'Sélectionnez un ou plusieurs détails ci-dessus pour enrichir l’annonce.'
                    )}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gap: 1.5 }}>
                  {activeFieldDefs.map((definition) => {
                    const label = formatFieldLabel(definition, t(`listingFields:${definition.key}`, definition.label));
                    const hasError = Boolean(fieldErrors[definition.key]);

                    return (
                      <Paper
                        key={definition.key}
                        variant="outlined"
                        ref={(element) => {
                          fieldRefs.current[definition.key] = element;
                        }}
                        sx={{
                          p: 2,
                          borderRadius: 2.5,
                          borderColor: hasError ? 'error.main' : 'rgba(0, 0, 0, 0.08)',
                          backgroundColor: hasError ? 'rgba(244, 67, 54, 0.03)' : '#ffffff',
                          boxShadow: hasError
                            ? '0 10px 22px rgba(244, 67, 54, 0.08)'
                            : '0 10px 22px rgba(15, 23, 42, 0.05)'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            mb: 1.5
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {label}
                            </Typography>
                            {definition.unit && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4 }}>
                                {definition.unit}
                              </Typography>
                            )}
                          </Box>

                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveField(definition.key)}
                            aria-label={t('common.remove', 'Supprimer')}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        {definition.field_type === 'boolean' ? (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={normalizedValue[definition.key] === true || normalizedValue[definition.key] === 'true'}
                                onChange={(event) => handleFieldChange(definition.key, event.target.checked)}
                              />
                            }
                            label={label}
                          />
                        ) : definition.field_type === 'select' ? (
                          <FormControl fullWidth size="small" error={hasError}>
                            <InputLabel>{label}</InputLabel>
                            <Select
                              value={normalizedValue[definition.key] ?? ''}
                              label={label}
                              renderValue={(selected) => {
                                if (!selected) return '';
                                const optMeta = definition._optionI18n?.find((o) => o.value === selected);
                                const optLabel = translateOption(selected, definition);
                                if (optMeta?.colorHex) {
                                  return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: optMeta.colorHex, border: '1px solid rgba(0,0,0,0.2)', flexShrink: 0 }} />
                                      {optLabel}
                                    </Box>
                                  );
                                }
                                return optLabel;
                              }}
                              onChange={(event) => handleFieldChange(definition.key, event.target.value)}
                            >
                              {definition.options.map((option) => {
                                const optMeta = definition._optionI18n?.find((o) => o.value === option);
                                return (
                                  <MenuItem key={option} value={option}>
                                    {optMeta?.colorHex ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: optMeta.colorHex, border: '1px solid rgba(0,0,0,0.2)', flexShrink: 0 }} />
                                        {translateOption(option, definition)}
                                      </Box>
                                    ) : (
                                      translateOption(option, definition)
                                    )}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                            {hasError && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>
                                {fieldErrors[definition.key]}
                              </Typography>
                            )}
                          </FormControl>
                        ) : definition.field_type === 'number' ? (
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={label}
                            value={normalizedValue[definition.key] ?? ''}
                            onChange={(event) => handleFieldChange(definition.key, event.target.value)}
                            error={hasError}
                            helperText={
                              hasError
                                ? fieldErrors[definition.key]
                                : definition.min !== undefined && definition.max !== undefined
                                  ? `${definition.min} – ${definition.max}${definition.unit ? ' ' + definition.unit : ''}`
                                  : ' '
                            }
                            inputProps={{
                              ...(definition.min !== undefined ? { min: definition.min } : {}),
                              ...(definition.max !== undefined ? { max: definition.max } : {})
                            }}
                            InputProps={definition.unit ? {
                              endAdornment: <InputAdornment position="end">{definition.unit}</InputAdornment>
                            } : undefined}
                          />
                        ) : (
                          <TextField
                            fullWidth
                            size="small"
                            label={label}
                            value={normalizedValue[definition.key] ?? ''}
                            onChange={(event) => handleFieldChange(definition.key, event.target.value)}
                            error={hasError}
                            helperText={hasError ? fieldErrors[definition.key] : ' '}
                          />
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
}

const ListingDetailsEditor = forwardRef(ListingDetailsEditorInner);

ListingDetailsEditor.displayName = 'ListingDetailsEditor';

export default ListingDetailsEditor;
